import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download, FolderOpen, Trash2 } from 'lucide-react';
import { GenericListView, ConfirmDialog } from '../../common';
import type { FilterConfig } from '../../common';
import { useHasAnyPermission } from '../../../hooks/usePermission';
import { PatientDocumentsService } from '../../../services/patientmr/patientDocuments.service';
import {
  LISTED_PATIENT_DOCUMENT_TYPES,
  PATIENT_DOCUMENT_TYPE_LABELS,
} from '../../../dtos/patientmr/patientDocuments.dto';
import type { PatientDocument, PatientDocumentType } from '../../../dtos/patientmr/patientDocuments.dto';
import type { PermissionString } from '../../../types/permissions';
import UploadDocumentDialog from './UploadDocumentDialog';
import AdmissionPacketPanel from './AdmissionPacketPanel';

interface PatientDocumentsProps {
  expanded: boolean;
  toggleExpanded: () => void;
  patientId?: string;
  readOnly?: boolean;
  /** Phase 19 F18: the admission packet signer rules (an adult patient may sign for themself) */
  patientDob?: string;
  patientName?: string;
}

/** PAT-09 documents permissions (the server enforces the same; plain = assigned patients, all = every patient) */
const DOCUMENT_UPLOAD_PERMS = ['patientmr:phi:documents:create', 'patientmr:phi:documents:createall'] as PermissionString[];
const DOCUMENT_DELETE_PERMS = ['patientmr:phi:documents:delete', 'patientmr:phi:documents:deleteall'] as PermissionString[];

const formatDate = (value?: string): string => {
  if (!value) return '';
  const [y, m, d] = value.slice(0, 10).split('-');
  return y && m && d ? `${m}/${d}/${y}` : value;
};

const formatSize = (bytes?: number): string => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Documents tab (Case Management): the patient's library of orders, consents, plans, guardianship papers. */
const PatientDocuments: React.FC<PatientDocumentsProps> = ({ expanded, toggleExpanded, patientId, readOnly = false, patientDob, patientName }) => {
  const canUpload = useHasAnyPermission(DOCUMENT_UPLOAD_PERMS) && !readOnly;
  const canDelete = useHasAnyPermission(DOCUMENT_DELETE_PERMS) && !readOnly;

  const [documents, setDocuments] = useState<PatientDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<'ALL' | PatientDocumentType>('ALL');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PatientDocument | null>(null);
  const [deleting, setDeleting] = useState(false);
  const loadedFor = useRef<string | null>(null);

  const load = useCallback(async (mode: 'load' | 'refresh' = 'load') => {
    if (!patientId) return;
    if (mode === 'refresh') setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const docs = await PatientDocumentsService.list(patientId);
      setDocuments([...docs].sort((a, b) => (a.documentDate < b.documentDate ? 1 : a.documentDate > b.documentDate ? -1 : 0)));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Failed to load documents.');
    } finally {
      setHasLoadedOnce(true);
      setLoading(false);
      setRefreshing(false);
    }
  }, [patientId]);

  useEffect(() => {
    if (!expanded || !patientId) return;
    if (loadedFor.current === patientId) return;
    loadedFor.current = patientId;
    void load();
  }, [expanded, patientId, load]);

  const filtered = useMemo(
    () => (typeFilter === 'ALL' ? documents : documents.filter((d) => d.type === typeFilter)),
    [documents, typeFilter]
  );

  const filters: FilterConfig[] = useMemo(() => [
    {
      key: 'type',
      label: 'Type',
      value: typeFilter,
      options: [
        { value: 'ALL', label: `All types (${documents.length})` },
        ...LISTED_PATIENT_DOCUMENT_TYPES.map((t) => ({
          value: t,
          label: `${PATIENT_DOCUMENT_TYPE_LABELS[t]} (${documents.filter((d) => d.type === t).length})`,
        })),
      ],
      onChange: (value: string) => setTypeFilter(value as 'ALL' | PatientDocumentType),
    },
  ], [typeFilter, documents]);

  const download = async (doc: PatientDocument) => {
    if (!patientId) return;
    try {
      const url = await PatientDocumentsService.getDownloadUrl(patientId, doc.documentId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Failed to download the document.');
    }
  };

  const confirmDelete = async () => {
    if (!patientId || !deleteTarget) return;
    setDeleting(true);
    try {
      await PatientDocumentsService.remove(patientId, deleteTarget.documentId);
      setDocuments((prev) => prev.filter((d) => d.documentId !== deleteTarget.documentId));
      setNotice('Document removed from the library.');
      setDeleteTarget(null);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Failed to delete the document.');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const banner = (
    <>
      {notice && (
        <div role="status" className="mb-3 flex items-center justify-between gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
          <span className="min-w-0 break-words">{notice}</span>
          <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss">×</button>
        </div>
      )}
      {patientId && (
        <AdmissionPacketPanel
          patientId={patientId}
          canSign={canUpload}
          patientDob={patientDob}
          patientName={patientName}
          onDocumentAdded={() => void load('refresh')}
        />
      )}
    </>
  );

  return (
    <>
      <GenericListView<PatientDocument>
        title="Documents"
        icon={<FolderOpen size={20} />}
        expanded={expanded}
        onToggleExpand={toggleExpanded}
        alertBanner={banner}
        items={filtered}
        keyExtractor={(d) => d.documentId}
        columns={[
          {
            key: 'type',
            label: 'Type',
            width: 'w-36',
            render: (d) => <span className="text-sm text-slate-700">{PATIENT_DOCUMENT_TYPE_LABELS[d.type] ?? d.type}</span>,
          },
          {
            key: 'date',
            label: 'Date',
            width: 'w-28',
            render: (d) => <span className="text-sm text-slate-700">{formatDate(d.documentDate)}</span>,
          },
          {
            key: 'title',
            label: 'Title',
            width: 'flex-1',
            render: (d) => (
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-slate-900">{d.title}</div>
                <div className="text-xs text-slate-500">{formatSize(d.size)}</div>
              </div>
            ),
          },
          {
            key: 'uploader',
            label: 'Uploaded by',
            width: 'w-40',
            render: (d) => (
              <div className="text-xs text-slate-600">
                <div>{d.uploadedByName || d.uploadedBy}</div>
                <div className="text-slate-400">{formatDate(d.uploadedAt)}</div>
              </div>
            ),
          },
        ]}
        actions={[
          {
            icon: <Download size={14} />,
            label: 'Download document',
            onClick: (d) => void download(d),
            className: 'p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors',
          },
          {
            icon: <Trash2 size={14} />,
            label: 'Delete document',
            onClick: (d) => setDeleteTarget(d),
            isVisible: canDelete,
            className: 'p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors',
          },
        ]}
        mobileCard={{
          primaryText: (d) => d.title,
          secondaryText: (d) => `${PATIENT_DOCUMENT_TYPE_LABELS[d.type] ?? d.type} • ${formatDate(d.documentDate)}`,
          tertiaryText: (d) => `Uploaded by ${d.uploadedByName || d.uploadedBy}`,
          actions: (d) => [
            { icon: <Download size={18} />, label: 'Download', onClick: () => void download(d) },
            ...(canDelete ? [{ icon: <Trash2 size={18} />, label: 'Delete', onClick: () => setDeleteTarget(d) }] : []),
          ],
        }}
        onRefresh={() => void load('refresh')}
        refreshing={refreshing}
        loading={loading}
        newButtonLabel="Upload Document"
        onNewClick={() => setUploadOpen(true)}
        showNew={canUpload && !!patientId}
        showSearch={false}
        filters={filters}
        resultCount={filtered.length}
        filterTotalCount={documents.length}
        error={error}
        onDismissError={() => setError(null)}
        hasLoadedOnce={hasLoadedOnce}
        emptyStateIcon={<FolderOpen size={48} className="text-gray-300" />}
        emptyStateTitle={typeFilter === 'ALL' ? 'No documents yet' : 'No documents of this type'}
        emptyStateMessage="Upload physician orders, consents, emergency plans and guardianship papers here."
      />

      {patientId && (
        <UploadDocumentDialog
          open={uploadOpen}
          patientId={patientId}
          onClose={() => setUploadOpen(false)}
          onUploaded={(doc) => {
            setUploadOpen(false);
            setNotice(`"${doc.title}" uploaded.`);
            void load('refresh');
          }}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete document?"
        message={deleteTarget ? `"${deleteTarget.title}" is removed from the library. The file is kept for the record.` : ''}
        confirmLabel="Delete"
        destructive
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
};

export default PatientDocuments;
